'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, DollarSign, PiggyBank, CreditCard, Target, Heart, Loader2, ArrowLeftRight, Wifi } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { getTransactionSummary } from '@/lib/transactions';
import { supabase } from '@/lib/supabase';

interface MetricCardProps {
  label: string;
  value: string;
  subtext: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon: React.ReactNode;
  variant: 'default' | 'success' | 'danger' | 'warning' | 'accent';
  size?: 'normal' | 'hero';
  progress?: number;
}

function MetricCard({ label, value, subtext, trend, trendValue, icon, variant, size = 'normal', progress }: MetricCardProps) {
  const variantStyles = {
    default: 'bg-card border-border',
    success: 'bg-card border-success/30 card-glow-success',
    danger: 'bg-card border-danger/30 card-glow-danger',
    warning: 'bg-card border-warning/30',
    accent: 'bg-card border-accent/30 card-glow-gold',
  };

  const iconBg = {
    default: 'bg-primary/30 text-foreground',
    success: 'bg-success/15 text-success',
    danger: 'bg-danger/15 text-danger',
    warning: 'bg-warning/15 text-warning',
    accent: 'bg-accent/15 text-accent',
  };

  return (
    <div className={`rounded-xl border p-5 flex flex-col justify-between transition-all duration-200 hover:border-accent/30 group ${variantStyles[variant]} ${size === 'hero' ? 'min-h-[140px]' : 'min-h-[120px]'}`}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-600 uppercase tracking-wide text-muted-foreground">{label}</p>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg[variant]}`}>
          {icon}
        </div>
      </div>
      <div>
        <p className={`font-700 font-mono-nums text-foreground ${size === 'hero' ? 'text-3xl' : 'text-2xl'}`}>{value}</p>
        <div className="flex items-center gap-2 mt-1.5">
          {trend && trendValue && (
            <span className={`flex items-center gap-0.5 text-xs font-600 ${trend === 'up' ? 'text-success' : trend === 'down' ? 'text-danger' : 'text-muted-foreground'}`}>
              {trend === 'up' ? <TrendingUp size={12} /> : trend === 'down' ? <TrendingDown size={12} /> : null}
              {trendValue}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{subtext}</span>
        </div>
        {progress !== undefined && (
          <div className="mt-3 progress-bar-bg h-1.5">
            <div
              className={`h-full rounded-full transition-all duration-700 ${variant === 'success' ? 'bg-success' : variant === 'danger' ? 'bg-danger' : variant === 'warning' ? 'bg-warning' : 'bg-accent'}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function MetricsBentoGrid() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadSummary = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const data = await getTransactionSummary(user.id);
      setSummary(data);
      setLastUpdated(new Date());
    } catch {
      // Use fallback display
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Real-time auto-update when transactions change
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('dashboard-metrics')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'transactions',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        loadSummary();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, loadSummary]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={`skel-${i}`} className={`rounded-xl border border-border bg-card p-5 min-h-[120px] flex items-center justify-center ${i === 0 ? 'md:col-span-2 lg:col-span-2' : ''}`}>
            <Loader2 size={20} className="text-accent animate-spin" />
          </div>
        ))}
      </div>
    );
  }

  const totalIncome = summary?.totalIncome ?? 0;
  const totalExpenses = summary?.totalExpenses ?? 0;
  const totalTransfers = summary?.totalTransfers ?? 0;
  const netCashFlow = summary?.netCashFlow ?? 0;
  const recurringExpenses = summary?.recurringExpenses ?? 0;
  const savingsRate = totalIncome > 0 ? ((netCashFlow / totalIncome) * 100) : 0;
  const debtPayments = summary?.byCategory?.['Debt Payment']?.expense ?? 0;
  const txCount = summary?.transactions?.length ?? 0;

  const healthScore = Math.min(100, Math.max(0,
    (savingsRate > 20 ? 30 : savingsRate > 10 ? 20 : 10) +
    (netCashFlow > 0 ? 30 : 10) +
    (debtPayments < totalExpenses * 0.3 ? 20 : 10) +
    (txCount > 0 ? 20 : 5)
  ));

  return (
    <div className="space-y-2">
      {lastUpdated && (
        <div className="flex items-center gap-1.5 text-xs text-success/80">
          <Wifi size={11} />
          <span>Live · auto-updates when transactions change · {lastUpdated.toLocaleTimeString()}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {/* Hero: Net Cash Flow */}
        <div className="md:col-span-2 lg:col-span-2">
          <MetricCard
            label="Net Cash Flow"
            value={`${netCashFlow >= 0 ? '+' : ''}$${Math.abs(netCashFlow).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            subtext={`${txCount} transactions`}
            trend={netCashFlow >= 0 ? 'up' : 'down'}
            trendValue={`${savingsRate.toFixed(1)}% savings rate`}
            icon={<TrendingUp size={18} />}
            variant={netCashFlow >= 0 ? 'success' : 'danger'}
            size="hero"
          />
        </div>

        <MetricCard
          label="Total Income"
          value={`$${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          subtext="deposits & additions"
          trend="up"
          trendValue="income only"
          icon={<DollarSign size={18} />}
          variant="success"
        />

        <MetricCard
          label="Total Expenses"
          value={`$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          subtext="withdrawals & deductions"
          trend={totalExpenses > totalIncome ? 'down' : 'neutral'}
          trendValue={totalExpenses > totalIncome ? 'Over budget' : 'On track'}
          icon={<CreditCard size={16} />}
          variant="danger"
        />

        <MetricCard
          label="Transfers"
          value={`$${totalTransfers.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          subtext="between accounts · not counted"
          trend="neutral"
          trendValue="excluded from flow"
          icon={<ArrowLeftRight size={16} />}
          variant="default"
        />

        <MetricCard
          label="Savings Rate"
          value={`${savingsRate.toFixed(1)}%`}
          subtext="target: 20%+"
          trend={savingsRate >= 20 ? 'up' : 'neutral'}
          trendValue={savingsRate >= 20 ? 'On target' : 'Below target'}
          icon={<PiggyBank size={18} />}
          variant="accent"
          progress={Math.min(savingsRate, 100)}
        />

        <MetricCard
          label="Recurring Expenses"
          value={`$${recurringExpenses.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          subtext="monthly fixed"
          trend="neutral"
          trendValue="subscriptions"
          icon={<Target size={18} />}
          variant="default"
          progress={totalExpenses > 0 ? (recurringExpenses / totalExpenses) * 100 : 0}
        />

        <MetricCard
          label="Health Score"
          value={`${healthScore}/100`}
          subtext={healthScore >= 70 ? 'Good — improving' : healthScore >= 50 ? 'Fair — needs work' : 'Needs attention'}
          trend={healthScore >= 70 ? 'up' : 'neutral'}
          trendValue={`${txCount} transactions`}
          icon={<Heart size={18} />}
          variant={healthScore >= 70 ? 'success' : healthScore >= 50 ? 'warning' : 'danger'}
          progress={healthScore}
        />
      </div>
    </div>
  );
}